import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { ArrowLeft } from 'lucide-react';

const LegalLayout = ({ title, children, testId }) => {
  const { isDark } = useTheme();
  return (
    <div
      className={`min-h-screen ${isDark ? 'bg-[#0F0F1A] text-white' : 'bg-[#FAFBFC] text-black'}`}
      data-testid={testId}
    >
      <div className={`sticky top-0 z-10 backdrop-blur-xl ${isDark ? 'bg-[#0F0F1A]/80 border-white/10' : 'bg-white/80 border-black/10'} border-b`}>
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-70 transition" data-testid="back-to-home">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Accueil</span>
          </Link>
          <h1 className="text-lg font-medium">{title}</h1>
          <div className="w-20" />
        </div>
      </div>
      <article className="max-w-3xl mx-auto px-6 py-16 prose prose-sm dark:prose-invert">
        {children}
      </article>
    </div>
  );
};

export const MentionsLegalesPage = () => (
  <LegalLayout title="Mentions légales" testId="legal-page">
    <h2 className="text-2xl font-light mb-6">Mentions légales</h2>
    <p className="opacity-70 mb-4">
      Skipya est en phase de bêta. Dernière mise à jour : 21/10/2026.
    </p>
    <h3 className="text-lg font-medium mt-8 mb-2">Éditeur</h3>
    <p className="opacity-80">
      Le site skipya.com et l'application Skipya sont édités par Hannah Taieb,
      entrepreneur individuel (EI), exerçant sous le nom Skipya.<br />
      Adresse : 4 rue Angélique Vérien, 92200 Neuilly-sur-Seine, France<br />
      SIREN : 107 369 837 — SIRET (établissement principal) : 107 369 837 00011<br />
      Immatriculée au Registre national des entreprises (RNE) le 08/07/2026<br />
      Code APE : 6202A — Conseil en systèmes et logiciels informatiques<br />
      Pour toute demande, contacte-nous via la page <Link to="/contact" className="text-[#81d8d0] underline">Contact</Link>.
    </p>
    <h3 className="text-lg font-medium mt-8 mb-2">Directrice de la publication</h3>
    <p className="opacity-80">Hannah Taieb.</p>
    <h3 className="text-lg font-medium mt-8 mb-2">Hébergement</h3>
    <p className="opacity-80">
      Site web : Vercel Inc., 440 N Barranca Ave #4133, Covina, CA 91723, États-Unis — vercel.com.<br />
      Serveur applicatif et données : Hostinger International Ltd, 61 Lordou Vironos Street,
      6023 Larnaca, Chypre — hostinger.com.
    </p>
    <h3 className="text-lg font-medium mt-8 mb-2">Propriété intellectuelle</h3>
    <p className="opacity-80">
      L'ensemble du contenu de ce site (textes, visuels, code) est protégé. Toute reproduction
      non autorisée est interdite.
    </p>
    <h3 className="text-lg font-medium mt-8 mb-2">Sources officielles utilisées</h3>
    <p className="opacity-80">
      Les calculs et informations administratives s'appuient sur des sources publiques :
      Service-Public.fr, OpenFisca, CAF, impots.gouv.fr, légifrance.
      Skipya n'est ni mandaté ni affilié à ces organismes.
    </p>
  </LegalLayout>
);

export const ConfidentialitePage = () => (
  <LegalLayout title="Confidentialité" testId="privacy-page">
    <h2 className="text-2xl font-light mb-6">Politique de confidentialité</h2>
    <p className="opacity-70 mb-4">
      Dernière mise à jour : 21/10/2026. Cette politique évoluera avant le lancement officiel.
    </p>
    <h3 className="text-lg font-medium mt-8 mb-2">Responsable du traitement</h3>
    <p className="opacity-80">
      Hannah Taieb, entrepreneur individuel (Skipya), SIREN 107 369 837,
      4 rue Angélique Vérien, 92200 Neuilly-sur-Seine, France.
    </p>
    <h3 className="text-lg font-medium mt-8 mb-2">Quelles données on collecte</h3>
    <ul className="opacity-80 list-disc pl-5 space-y-1">
      <li>Compte : email, mot de passe (haché), prénom et nom si renseignés.</li>
      <li>Profil administratif (volontaire) : situation, revenus, loyer pour le simulateur d'aides.</li>
      <li>Documents : les fichiers que tu téléverses dans ton coffre-fort personnel.</li>
      <li>Usage : statistiques anonymes pour améliorer l'expérience (PostHog).</li>
    </ul>
    <h3 className="text-lg font-medium mt-8 mb-2">Ce qu'on ne fait pas</h3>
    <ul className="opacity-80 list-disc pl-5 space-y-1">
      <li>On ne vend ni ne loue tes données.</li>
      <li>On ne lit pas tes documents manuellement.</li>
      <li>L'OCR de tes documents tourne sur nos serveurs. En revanche, les messages du copilote, la génération de lettres et les suggestions fiscales sont traités par un fournisseur d'intelligence artificielle tiers.</li>
    </ul>
    <h3 className="text-lg font-medium mt-8 mb-2">Tes droits (RGPD)</h3>
    <p className="opacity-80">
      Tu peux à tout moment demander l'accès, la rectification, l'effacement ou la portabilité de tes données.
      Écris-nous via la page <Link to="/contact" className="text-[#81d8d0] underline">Contact</Link>.
    </p>
    <h3 className="text-lg font-medium mt-8 mb-2">Sécurité</h3>
    <p className="opacity-80">
      Connexion HTTPS, mots de passe hachés (bcrypt), JWT pour l'authentification.
      Les détails techniques sont sur la page <Link to="/etat-du-projet" className="text-[#81d8d0] underline">État du projet</Link>.
    </p>
  </LegalLayout>
);

export const ContactPage = () => (
  <LegalLayout title="Contact" testId="contact-page">
    <h2 className="text-2xl font-light mb-6">Nous écrire</h2>
    <p className="opacity-80 mb-6">
      Une question, un bug, une suggestion, une demande pro ou un retour RGPD ?
      Le formulaire arrive bientôt. En attendant, le mieux est de nous envoyer un email.
    </p>
    <div className="rounded-2xl p-6 bg-[#81d8d0]/10 border border-[#81d8d0]/30">
      <div className="text-sm opacity-70 mb-1">Email</div>
      <a href="mailto:hello@skipya.com" className="text-xl font-medium text-[#81d8d0] hover:underline" data-testid="contact-email">
        hello@skipya.com
      </a>
    </div>
    <h3 className="text-lg font-medium mt-8 mb-2">Demandes pro / partenariats</h3>
    <p className="opacity-80">
      Précise simplement "Pro" en objet, on revient vers toi rapidement.
    </p>
    <h3 className="text-lg font-medium mt-8 mb-2">Bug, suggestion</h3>
    <p className="opacity-80">
      Décris le contexte (page, navigateur, action effectuée) — ça nous aide énormément.
    </p>
  </LegalLayout>
);
